import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Mutation for `POST /v1/admin/triggers/{licenseId}` — fans out
 * orchestration calls (main-queue / fusion-metrics / screenshot-
 * capture / github-backup) for a license. Replaces the legacy
 * `wf-adminconsole` `TriggerActionModal` server action.
 *
 * Backend response is an aggregate of per-action results; surface
 * the array verbatim so the modal can show each toggle's outcome.
 */
export interface TriggerInput {
  licenseId: string;
  mainQueue?: boolean;
  fusionMetrics?: boolean;
  screenshotCapture?: boolean;
  githubBackup?: boolean;
  forceRefresh?: boolean;
}

export interface TriggerActionResult {
  action: 'mainQueue' | 'fusionMetrics' | 'screenshotCapture' | 'githubBackup';
  ok: boolean;
  status: number;
  response?: unknown;
  error?: string;
}

export interface TriggerResponse {
  licenseId: string;
  forceRefresh: boolean;
  results: TriggerActionResult[];
}

export function useTriggerLicenseActions(): UseMutationResult<
  TriggerResponse,
  Error,
  TriggerInput
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ licenseId, ...body }: TriggerInput) => {
      if (!api) throw new Error('useTriggerLicenseActions: api client unavailable');
      return api.post<TriggerResponse>(`/v1/admin/triggers/${licenseId}`, body);
    },
  });
}
